import { useCallback } from 'react';

import { gql, useApolloClient, useMutation } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type {
  NexoyaContentFilterInput,
  NexoyaContentLabelCategoryValue,
  NexoyaContentLabelCategoryValueCreateInput,
  NexoyaContentLabelCategoryValueUpdateInput,
  NexoyaMutationCreateContentLabelCategoryValueArgs,
  NexoyaMutationDeleteContentLabelCategoryValueArgs,
  NexoyaMutationUpdateContentLabelCategoryValueArgs,
} from '../../types';

import {
  CONTENT_LABEL_MUTATION_CONTEXT,
  activeStageReads,
  contentLabelCategoryCacheId,
  evictCategoryStages,
} from './mutationEffects';
import { CONTENT_LABEL_CATEGORY_VALUE_FRAGMENT } from './queryContentLabelCategories';
import { evictDeletePreviews } from './queryContentLabelDeletePreviews';

/**
 * Label CRUD. A "label" in the UI is a `ContentLabelCategoryValue` on the wire.
 *
 * `filters` are `ContentFilterInput[]` — the same filter engine content rules
 * use — built from the UI's draft by `labelCategoryMapper.toLabelValueFiltersInput`
 * (rule clauses plus the hidden exclusion clause), so these hooks stay
 * wire-typed.
 *
 * "No rule" has exactly one spelling per operation, and it is never `filters:
 * []` (undefined matcher semantics server-side): the key is OMITTED on create and
 * sent as `null` on update, where an omitted key means "leave the rule alone".
 *
 * Cache effects rather than refetching the category list (see `mutationEffects.ts`):
 * - create: a reference to the new value is appended to its category's `values`,
 *   and the category's stage reads are refreshed (a new rule moves contents).
 * - update: the returned value is normalized, so a rename shows everywhere by
 *   itself; an update that SENT filters also refreshes the category's stage reads.
 * - delete: the value leaves its category's `values` and is evicted, and the
 *   category's stage reads are refreshed — deleting a value cascades its
 *   assignment rows server-side. A `false` answer deleted nothing, so the cache
 *   is left alone.
 *
 * "Refreshing" the stage reads is both halves of `mutationEffects.ts`: the
 * category's cached stage data is evicted, and every stage read on screen is
 * asked again with a request of its own.
 */

export const CREATE_CONTENT_LABEL_CATEGORY_VALUE_MUTATION = gql`
  mutation CreateContentLabelCategoryValue($teamId: Int!, $create: ContentLabelCategoryValueCreateInput!) {
    createContentLabelCategoryValue(teamId: $teamId, create: $create) {
      ...ContentLabelCategoryValueFields
    }
  }
  ${CONTENT_LABEL_CATEGORY_VALUE_FRAGMENT}
`;

export const UPDATE_CONTENT_LABEL_CATEGORY_VALUE_MUTATION = gql`
  mutation UpdateContentLabelCategoryValue($teamId: Int!, $update: ContentLabelCategoryValueUpdateInput!) {
    updateContentLabelCategoryValue(teamId: $teamId, update: $update) {
      ...ContentLabelCategoryValueFields
    }
  }
  ${CONTENT_LABEL_CATEGORY_VALUE_FRAGMENT}
`;

export const DELETE_CONTENT_LABEL_CATEGORY_VALUE_MUTATION = gql`
  mutation DeleteContentLabelCategoryValue($teamId: Int!, $delete: ContentLabelCategoryValueDeleteInput!) {
    deleteContentLabelCategoryValue(teamId: $teamId, delete: $delete)
  }
`;

export type CreateContentLabelCategoryValueResponse = {
  createContentLabelCategoryValue: NexoyaContentLabelCategoryValue;
};
export type UpdateContentLabelCategoryValueResponse = {
  updateContentLabelCategoryValue: NexoyaContentLabelCategoryValue;
};
export type DeleteContentLabelCategoryValueResponse = { deleteContentLabelCategoryValue: boolean };

export function useCreateContentLabelCategoryValue() {
  const { teamId } = useTeam();
  const client = useApolloClient();

  const [mutate, result] = useMutation<
    CreateContentLabelCategoryValueResponse,
    NexoyaMutationCreateContentLabelCategoryValueArgs
  >(CREATE_CONTENT_LABEL_CATEGORY_VALUE_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  const createContentLabelCategoryValue = useCallback(
    ({
      contentLabelCategoryId,
      name,
      filters,
    }: {
      contentLabelCategoryId: string;
      name: string;
      /** No rule — null, undefined or empty — omits the key. */
      filters?: NexoyaContentFilterInput[] | null;
    }) => {
      const create: NexoyaContentLabelCategoryValueCreateInput = {
        contentLabelCategoryId,
        name,
        ...(filters?.length ? { filters } : {}),
      };

      return mutate({
        variables: { teamId, create },
        update(cache, { data }) {
          const created = data?.createContentLabelCategoryValue;
          if (!created) return;

          cache.modify<{ values: NexoyaContentLabelCategoryValue[] }>({
            id: contentLabelCategoryCacheId(cache, contentLabelCategoryId),
            fields: {
              values(existing = [], { readField, toReference }) {
                const alreadyListed = existing.some(
                  (ref) => readField('contentLabelCategoryValueId', ref) === created.contentLabelCategoryValueId,
                );
                const createdRef = toReference(created);
                return alreadyListed || !createdRef ? existing : [...existing, createdRef];
              },
            },
          });
          evictCategoryStages(cache, contentLabelCategoryId);
        },
        refetchQueries: ({ data }) => (data?.createContentLabelCategoryValue ? activeStageReads(client) : []),
      });
    },
    [client, mutate, teamId],
  );

  return [createContentLabelCategoryValue, result] as const;
}

export function useUpdateContentLabelCategoryValue() {
  const { teamId } = useTeam();
  const client = useApolloClient();

  const [mutate, result] = useMutation<
    UpdateContentLabelCategoryValueResponse,
    NexoyaMutationUpdateContentLabelCategoryValueArgs
  >(UPDATE_CONTENT_LABEL_CATEGORY_VALUE_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  /**
   * `name` and `filters` are both optional on the input, so an omitted key means
   * "leave it alone". Renaming a label must not silently wipe its rule, hence
   * each key is only sent when the caller passed it — and a rule the caller
   * cleared (`null` or `[]`) is sent as `null`, never as `[]`.
   */
  const updateContentLabelCategoryValue = useCallback(
    ({
      contentLabelCategoryId,
      contentLabelCategoryValueId,
      name,
      filters,
    }: {
      contentLabelCategoryId: string;
      contentLabelCategoryValueId: string;
      name?: string;
      filters?: NexoyaContentFilterInput[] | null;
    }) => {
      const sendsFilters = filters !== undefined;
      const update: NexoyaContentLabelCategoryValueUpdateInput = {
        contentLabelCategoryId,
        contentLabelCategoryValueId,
        ...(name === undefined ? {} : { name }),
        ...(sendsFilters ? { filters: filters?.length ? filters : null } : {}),
      };

      return mutate({
        variables: { teamId, update },
        update(cache, { data }) {
          // The value itself is normalized from the response; only a new RULE
          // moves contents between the category's stages.
          if (!data?.updateContentLabelCategoryValue || !sendsFilters) return;
          evictCategoryStages(cache, contentLabelCategoryId);
        },
        refetchQueries: ({ data }) =>
          data?.updateContentLabelCategoryValue && sendsFilters ? activeStageReads(client) : [],
      });
    },
    [client, mutate, teamId],
  );

  return [updateContentLabelCategoryValue, result] as const;
}

export function useDeleteContentLabelCategoryValue() {
  const { teamId } = useTeam();
  const client = useApolloClient();

  const [mutate, result] = useMutation<
    DeleteContentLabelCategoryValueResponse,
    NexoyaMutationDeleteContentLabelCategoryValueArgs
  >(DELETE_CONTENT_LABEL_CATEGORY_VALUE_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  const deleteContentLabelCategoryValue = useCallback(
    ({
      contentLabelCategoryId,
      contentLabelCategoryValueId,
    }: {
      contentLabelCategoryId: string;
      contentLabelCategoryValueId: string;
    }) =>
      mutate({
        variables: { teamId, delete: { contentLabelCategoryId, contentLabelCategoryValueId } },
        update(cache, { data }) {
          if (!data?.deleteContentLabelCategoryValue) return;

          cache.modify<{ values: NexoyaContentLabelCategoryValue[] }>({
            id: contentLabelCategoryCacheId(cache, contentLabelCategoryId),
            fields: {
              values: (existing = [], { readField }) =>
                existing.filter((ref) => readField('contentLabelCategoryValueId', ref) !== contentLabelCategoryValueId),
            },
          });
          cache.evict({ id: cache.identify({ __typename: 'ContentLabelCategoryValue', contentLabelCategoryValueId }) });
          evictCategoryStages(cache, contentLabelCategoryId);
          // "Delete label and constraints" can change what the categories' previews count.
          evictDeletePreviews(cache);
          cache.gc();
        },
        refetchQueries: ({ data }) => (data?.deleteContentLabelCategoryValue ? activeStageReads(client) : []),
      }),
    [client, mutate, teamId],
  );

  return [deleteContentLabelCategoryValue, result] as const;
}
